import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { partnerService } from '@/services/partner';
import { PartnerEmail } from '@/types/partner';
import { getClientDisplayName } from '../utils/clientDisplay';
import { clientFormSchema, ClientFormData } from '../utils/clientFormSchema';

interface EditClientModalProps {
  isOpen: boolean;
  partnerId: string;
  client: PartnerEmail | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditClientModal({
  isOpen,
  partnerId,
  client,
  onClose,
  onSuccess,
}: EditClientModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (client && isOpen) {
      reset({
        name: client.name?.trim() || getClientDisplayName(client),
        email: client.email,
        whatsapp: client.whatsapp ?? '',
      });
    }
  }, [client, isOpen, reset]);

  const onSubmit = async (data: ClientFormData) => {
    if (!client) return;
    setIsLoading(true);
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      await partnerService.updateUser(partnerId, client.id, {
        name: data.name.trim(),
        email: normalizedEmail,
        whatsapp: data.whatsapp?.trim() || undefined,
      });
      toast({ title: 'Cliente atualizado com sucesso!' });
      onSuccess();
      onClose();
    } catch {
      toast({
        variant: 'error',
        title: 'Não foi possível editar o cliente',
        description: 'Tente novamente ou entre em contato com o suporte.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle>Editar cliente</DialogTitle>
          <DialogDescription>
            Atualize as informações do cliente. Alterações no e-mail não reenviam o link de criação
            de senha.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Nome *</label>
            <Input
              {...register('name')}
              placeholder="Digite o nome do cliente"
              className={errors.name ? 'border-red-500' : ''}
              error={errors.name?.message}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">E-mail *</label>
            <Input
              type="email"
              {...register('email')}
              placeholder="Digite o e-mail do cliente"
              className={errors.email ? 'border-red-500' : ''}
              error={errors.email?.message}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">WhatsApp</label>
            <div className="flex gap-2">
              <div className="w-20 shrink-0 flex items-center justify-center rounded-md border border-input bg-muted px-3 text-sm text-gray-600">
                +55
              </div>
              <Input
                {...register('whatsapp')}
                placeholder="Digite o número do cliente"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="dark" disabled={isLoading || !isValid || !isDirty}>
              {isLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
